/**
 * Handle server side fetching multiple product data
 * Copied and modified from Product-Server.ts
 */

import { getStoreLocale } from '@/data/Content/StoreLocale-Server';
import { PRODUCT_DATA_KEY, productFetcher } from '@/data/Content/_Product';
import { getLocalization } from '@/data/Localization-Server';
import { getContractIdParamFromContext, getSettings } from '@/data/Settings-Server';
import { getUser } from '@/data/User-Server';
import { getServerCacheScope } from '@/data/cache/getServerCacheScope';
import { SearchProfiles } from '@/data/constants/commonIntegrationConstants';
import { Cache } from '@/data/types/Cache';
import { ProductType } from '@/data/types/Product';
import { getPhysicalStoreId } from '@/data/utils/commonIntegrationUtils';
import { constructRequestParamsWithPreviewToken } from '@/data/utils/constructRequestParams';
import { extractContentsArray } from '@/data/utils/extractContentsArray';
import { getCurrencyParamFromContext } from '@/data/utils/getCurrencyParamFromContext';
import { getServerSideCommon } from '@/data/utils/getServerSideCommon';
import { shrink } from '@/data/utils/keyUtil';
import { mapProductData } from '@/data/utils/mapProductData';
import { RequestParams } from 'integration/generated/query/http-client';
import { GetServerSidePropsContext } from 'next';
import { unstable_serialize as unstableSerialize } from 'swr';

const DATA_KEY = PRODUCT_DATA_KEY;
export type ProductFetchType = {
	product: ProductType | null;
};

export const getProductsSWRKey = ({
	storeId,
	lookupKey,
	lookupValue,
	catalogId,
	langId,
	physicalStoreId,
	profileName,
	marketId,
	stLocId,
	contractId,
	currency,
}: {
	storeId: string;
	lookupKey: string;
	lookupValue: string[];
	catalogId: string;
	langId: string;
	physicalStoreId: string;
	profileName?: string;
	marketId?: string;
	stLocId?: string;
	contractId?: string;
	currency?: string;
}) => {
	const query: {
		storeId: string;
		catalogId: string;
		[key: string]: string | boolean | (string | number)[];
	} = {
		storeId,
		[lookupKey]: Array.isArray(lookupValue) ? lookupValue : [lookupValue],
		catalogId,
		langId,
		physicalStoreId,
		...(profileName && { profileName }),
		...(marketId && { marketId }),
		...(stLocId && { stLocId }),
		...(contractId && { contractId }),
		...(currency && { currency }),
	};
	return query;
};

export const fetcherForProducts =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async (
		query: {
			storeId: string;
			catalogId: string;
			[key: string]: string | boolean | (string | number)[];
		},
		params: RequestParams
	): Promise<ProductFetchType[]> => {
		const response = await productFetcher(pub, context)(query, params);
		const products = extractContentsArray(response);
		return products.length > 0
			? products.map((p: ProductType) => ({ product: p ? mapProductData(p) : null }))
			: [];
	};

export const getProductsByKeyType = async (
	cache: Cache,
	lookupKey: string,
	lookupValue: string[],
	context: GetServerSidePropsContext,
	profileName: string = SearchProfiles.HDM_FIND_PRODUCT_BY_PART_NUMBER_DETAILS,
	stLocId?: string,
	marketId?: string
) => {
	const settings = await getSettings(cache, context);
	const user = await getUser(cache, context);
	const { localeName: locale } = await getStoreLocale({ cache, context });
	const routes = await getLocalization(cache, locale, 'Routes');
	const { storeId, langId, defaultCatalogId: catalogId } = getServerSideCommon(settings, context);
	const physicalStoreId = getPhysicalStoreId(context);
	const query = getProductsSWRKey({
		storeId,
		lookupKey,
		lookupValue,
		catalogId,
		langId,
		physicalStoreId,
		profileName,
		stLocId,
		marketId,
		...getContractIdParamFromContext(user?.context),
		...getCurrencyParamFromContext(user?.context),
	});
	const key = unstableSerialize([shrink(query), DATA_KEY]);
	const params = constructRequestParamsWithPreviewToken({ context, settings, routes });
	const cacheScope = getServerCacheScope(context, user.context);
	const value =
		(cache.get(key, cacheScope) as Promise<ProductFetchType | undefined>) ||
		fetcherForProducts(false, context)(query, params);
	cache.set(key, value, cacheScope);
	return (await value)?.product ?? undefined;
};

export const getProducts = async (
	map: Cache,
	partNumbers: string[],
	context: GetServerSidePropsContext,
	profileName?: string,
	stLocId?: string,
	marketId?: string
) =>
	await getProductsByKeyType(
		map,
		'partNumber',
		partNumbers,
		context,
		profileName,
		stLocId,
		marketId
	);
